import type { Pages } from "./pages"
import type { Entity } from "@/utils"
import { invariant } from "@/utils"

/** Where a just-created row falls among the loaded ones, which run newest first. */
export type Placement = "empty" | "loaded" | "newer" | "between" | "older"

// UUIDv7 ids sort by creation time, so comparing them orders rows the way the list does.
export const placeOf = <TItem extends Entity>(
  pages: Pages<TItem>,
  row: TItem,
): Placement => {
  const loaded = pages.pages.flatMap((page) => page.items)
  const first = loaded.at(0)
  const last = loaded.at(-1)

  if (!first || !last) {
    return "empty"
  }
  if (loaded.some(({ id }) => id === row.id)) {
    return "loaded"
  }
  if (row.id > first.id) {
    return "newer"
  }

  return row.id > last.id ? "between" : "older"
}

const isEndLoaded = <TItem>(pages: Pages<TItem>) =>
  pages.pages.at(-1)?.nextCursor === null

/**
 * Inserts a row in id order among the loaded ones, or after the last once
 * nothing older remains to load; any other row is left alone.
 */
export const spliceRow = <TItem extends Entity>(
  pages: Pages<TItem>,
  row: TItem,
) => {
  const place = placeOf(pages, row)
  // A copy: Immer freezes the inserted row, and a replay patches it in the same produce.
  const copy = { ...row }

  if (place === "older" && isEndLoaded(pages)) {
    pages.pages.at(-1)?.items.push(copy)

    return
  }
  if (place !== "between") {
    return
  }

  const page = pages.pages.find((candidate) =>
    candidate.items.some(({ id }) => id < row.id),
  )
  invariant(page, "A row between loaded ones has an older one after it")
  page.items.splice(
    page.items.findIndex(({ id }) => id < row.id),
    0,
    copy,
  )
}

/**
 * After a page of newer rows lands at the front: an empty one is dropped, and
 * a new first page becomes the head a refetch starts from.
 */
export const settleNewerPage = <TItem>(pages: Pages<TItem>) => {
  if (pages.pages.length > 1 && !pages.pages[0]?.items.length) {
    pages.pages.shift()
    pages.pageParams.shift()

    return
  }

  pages.pageParams[0] = null
}
